import React, { useState, useEffect } from "react";
import AdminHeader from "./Header";
import "../../styles/Super.css";
import { adminReqConfig, api } from "../../include/api";
import { Link } from "react-router-dom";
import DashboardOrders from "./DashboardOrders";
import PerformanceChart from "../charts/apex-charts/PerformanceChat";

const Dashboard = () => {
  const [orderSummary, setOrderSummary] = useState([]);

  const [chartData, setChartData] = useState({ labels: [], series: [] });
  const [loading, setLoading] = useState(true);
  const [chartPeriod, setChartPeriod] = useState("1Y");
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1–12

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  const yearOptions = Array.from(
    { length: 10 },
    (_, i) => currentYear - i,
  ).reverse();

  const monthOptions = [
    { value: 1, label: "Jan" },
    { value: 2, label: "Feb" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Apr" },
    { value: 5, label: "May" },
    { value: 6, label: "Jun" },
    { value: 7, label: "Jul" },
    { value: 8, label: "Aug" },
    { value: 9, label: "Sep" },
    { value: 10, label: "Oct" },
    { value: 11, label: "Nov" },
    { value: 12, label: "Dec" },
  ];

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        let labels = [];
        let allSalesData = [];
        let monthsToShow = [];

        if (chartPeriod === "1M") {
          // Use selected year & month
          labels = Array.from({ length: 31 }, (_, i) => (i + 1).toString());
          const res = await api.get(
            `/orders/?flag=monthgraph&year=${selectedYear}&month=${selectedMonth}`,
            adminReqConfig(),
          );
          allSalesData = res.data.data || [];
        } else if (chartPeriod === "1Y") {
          // Use selected year
          for (let month = 1; month <= 12; month++) {
            monthsToShow.push({ year: selectedYear, month });
          }
          labels = monthsToShow.map((m) =>
            new Date(m.year, m.month - 1, 1).toLocaleString("default", {
              month: "short",
            }),
          );
          const res = await api.get(
            `/orders/?flag=yeargraph&year=${selectedYear}`,
            adminReqConfig(),
          );
          allSalesData = res.data.data || [];
        } else {
          // 6M: auto (last 6 months from today)
          const now = new Date();
          const currentYear = now.getFullYear();
          const currentMonth = now.getMonth() + 1;

          for (let i = 5; i >= 0; i--) {
            let month = currentMonth - i;
            let year = currentYear;
            if (month <= 0) {
              month += 12;
              year -= 1;
            }
            monthsToShow.push({ year, month });
          }
          labels = monthsToShow.map((m) =>
            new Date(m.year, m.month - 1, 1).toLocaleString("default", {
              month: "short",
            }),
          );
          const yearsNeeded = [...new Set(monthsToShow.map((m) => m.year))];
          for (const y of yearsNeeded) {
            const res = await api.get(
              `/orders/?flag=yeargraph&year=${y}`,
              adminReqConfig(),
            );
            allSalesData = [...allSalesData, ...(res.data.data || [])];
          }
        }

        // ... rest of your code (categories, series building) remains the same

        const catRes = await api.get(
          "/category/?flag=exceptHome",
          adminReqConfig(),
        );
        const categories = catRes.data.data || [];
        const topLevelCategories = categories.filter((c) => c.parentId === 0);
        const categoryNames = topLevelCategories.map((c) => c.name);

        const salesByCategory = {};
        allSalesData.forEach((item) => {
          if (!salesByCategory[item.name]) {
            salesByCategory[item.name] = {};
          }
          const key = chartPeriod === "1M" ? item.dayNo : item.monthNo;
          salesByCategory[item.name][key] = item.totQty;
        });

        const allCatNames = [
          "All",
          ...categoryNames.filter((name) => name !== "All"),
        ];
        const maxPoints = chartPeriod === "1M" ? 31 : labels.length;

        const series = allCatNames.map((catName) => {
          const data = Array(maxPoints).fill(0);
          for (let i = 0; i < maxPoints; i++) {
            const key = chartPeriod === "1M" ? i + 1 : monthsToShow[i]?.month;
            data[i] = salesByCategory[catName]?.[key] || 0;
          }
            const displayName = catName === "All" ? "Orders" : catName;

          return {
            name: displayName,
            type: catName === "All" ? "bar" : "line",
            data,
            color: getCategoryColor(catName),
          };
        });

        setChartData({ labels, series });
      } catch (err) {
        console.error("Failed to load performance ", err);
        setChartData({ labels: [], series: [] });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [chartPeriod, selectedYear, selectedMonth]); // ← added dependencies

  const getSummary = () => {
    api
      .get("/orders/?flag=orderSummary", adminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          setOrderSummary(response.data.data);
        }
      })
      .catch(() => setOrderSummary([]));
  };

  useEffect(() => {
    getSummary();
  }, []);

  const getGrowthPercent = (current, last) => {
    if (last === 0) {
      if (current === 0) return 0;
      return 100;
    }

    return Number((((current - last) / last) * 100).toFixed(1));
  };

  const orderGrowth = getGrowthPercent(
    orderSummary?.[0]?.currentWeekOrders,
    orderSummary?.[0]?.lastWeekOrders,
  );
  const newLeadGrowth = getGrowthPercent(
    orderSummary?.[0]?.currentWeekLeads,
    orderSummary?.[0]?.lastWeekLeads,
  );
  const customerGrowth = getGrowthPercent(
    orderSummary?.[0]?.currentWeekCustomer,
    orderSummary?.[0]?.lastWeekCustomer,
  );
  const productGrowth = getGrowthPercent(
    orderSummary?.[0]?.currentWeekProducts,
    orderSummary?.[0]?.lastWeekProducts,
  );

  // Simple string-to-hsl hash
  const getStringHash = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  };

  const getCategoryColor = (name) => {
    if (name === "All") {
      return "#2696D5";
    }

    // Rule 3: Generate consistent color for unknown categories
    const hash = getStringHash(name);
    const hue = hash % 360;
    const saturation = 70 + (hash % 3) * 2; // 70–90%
    const lightness = 45 + (Math.floor(hash / 100) % 5); // 45–65%
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  return (
    <AdminHeader title={"DASHBOARD"}>
      <div className="content-wrapper p-0">
        <section
          style={{
            backgroundColor: "#F1F4F9",
            padding: "30px",
            marginTop: "70px",
            marginBottom: "40px",
          }}
        >
          <div className="row g-3 mb-3">
            <div
              className="col-lg-4"
              style={{ height: "300px", marginBottom: "60px" }}
            >
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="dash-status-card shadow-sm bg-white">
                    <div className="mt-5" style={{ padding: "15px" }}>
                      <h6>Total Orders</h6>
                      <div className="d-flex justify-content-between mt-8">
                        <div>
                          <p>
                            {orderSummary?.[0]?.totalOrders.toLocaleString()}
                          </p>
                        </div>
                        <div className="dash-status-card-img-div">
                          <img
                            src="/assets/images/tot-orders.png"
                            style={{ width: "20px", height: "20px" }}
                          />
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: "11px" }}>
                      <div
                        style={{
                          padding: "8px 15px",
                          background: "#F0F7FD",
                          borderBottomLeftRadius: "8px",
                          borderBottomRightRadius: "8px",
                        }}
                      >
                        <div className="mt-5">
                          <GrowthIndicator value={orderGrowth} />
                        </div>
                        <div className="dash-status-bottom-col d-flex justify-content-between align-items-center mt-2">
                          <p>Last Week</p>
                          <Link
                            className="dash-status-botton-btn"
                            to="/admin/sales/orders"
                          >
                            View more
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* New Leads */}
                <div className="col-md-6">
                  <div className="dash-status-card shadow-sm bg-white">
                    <div className="mt-5" style={{ padding: "15px" }}>
                      <h6>New Leads</h6>
                      <div className="d-flex justify-content-between mt-8">
                        <div>
                          <p>{orderSummary?.[0]?.newLeads.toLocaleString()}</p>
                        </div>
                        <div className="dash-status-card-img-div">
                          <img
                            src="/assets/images/leads1.png"
                            style={{ width: "20px", height: "20px" }}
                          />
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: "11px" }}>
                      <div
                        style={{
                          padding: "8px 15px",
                          background: "#F0F7FD",
                          borderBottomLeftRadius: "8px",
                          borderBottomRightRadius: "8px",
                        }}
                      >
                        <div className="mt-5">
                          <GrowthIndicator value={newLeadGrowth} />
                        </div>
                        <div className="dash-status-bottom-col d-flex justify-content-between align-items-center mt-2">
                          <p>Last Week</p>
                          <Link
                            className="dash-status-botton-btn"
                            to="/admin/customer/customerList/?leads=1"
                          >
                            View more
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Total Customers */}
                <div className="col-md-6">
                  <div className="dash-status-card shadow-sm bg-white">
                    <div className="mt-5" style={{ padding: "15px" }}>
                      <h6>Total Customers</h6>
                      <div className="d-flex justify-content-between mt-8">
                        <div>
                          <p>
                            {orderSummary?.[0]?.totalCustomers.toLocaleString()}
                          </p>
                        </div>
                        <div className="dash-status-card-img-div">
                          <img
                            src="/assets/images/tot-cus1.png"
                            style={{ width: "20px", height: "20px" }}
                          />
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: "11px" }}>
                      <div
                        style={{
                          padding: "8px 15px",
                          background: "#F0F7FD",
                          borderBottomLeftRadius: "8px",
                          borderBottomRightRadius: "8px",
                        }}
                      >
                        <div className="mt-5">
                          <GrowthIndicator value={customerGrowth} />
                        </div>
                        <div className="dash-status-bottom-col d-flex justify-content-between align-items-center mt-2">
                          <p>Last Week</p>
                          <Link
                            className="dash-status-botton-btn"
                            to="/admin/customer/customerList"
                          >
                            View more
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Total Products */}
                <div className="col-md-6">
                  <div className="dash-status-card shadow-sm bg-white">
                    <div className="mt-5" style={{ padding: "15px" }}>
                      <h6>Total Products</h6>
                      <div className="d-flex justify-content-between mt-8">
                        <div>
                          <p>
                            {orderSummary?.[0]?.totalProducts.toLocaleString()}
                          </p>
                        </div>
                        <div className="dash-status-card-img-div">
                          <img
                            src="/assets/images/tot-order.png"
                            style={{ width: "20px", height: "20px" }}
                          />
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: "11px" }}>
                      <div
                        style={{
                          padding: "8px 15px",
                          background: "#F0F7FD",
                          borderBottomLeftRadius: "8px",
                          borderBottomRightRadius: "8px",
                        }}
                      >
                        <div className="mt-5">
                          <GrowthIndicator value={productGrowth} />
                        </div>
                        <div className="dash-status-bottom-col d-flex justify-content-between align-items-center mt-2">
                          <p>Last Week</p>
                          <Link
                            className="dash-status-botton-btn"
                            to="/admin/product/productList"
                          >
                            View more
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - PERFORMANCE CHART */}
            <div className="col-lg-8">
              <div
                className="chart-card shadow-sm bg-white"
                style={{
                  borderRadius: "8px",
                  padding: "20px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  marginTop:'5px'
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-14">
                  <h6 style={{ fontSize: "16px", fontWeight: "600" }}>
                    Performance
                  </h6>
                  <div className="d-flex align-items-center gap-2">
                    {/* Period Buttons */}
                    <div>
                      <button
                        className={` me-1 ${chartPeriod === "1M" ? "dash-btn-active" : ""}`}
                        style={{
                          fontSize: "12px",
                          border: "2px solid #D5DCE0",
                          padding: "5px 15px",
                          borderRadius: "5px",
                        }}
                        onClick={() => setChartPeriod("1M")}
                      >
                        1M
                      </button>
                      {/* <button
                        className={` me-1 ${chartPeriod === "6M" ? "dash-btn-active" : ""}`}
                        style={{
                          fontSize: "12px",
                          border: "2px solid #D5DCE0",
                          padding: "5px 15px",
                          borderRadius: "5px",
                        }}
                        onClick={() => setChartPeriod("6M")}
                      >
                        6M
                      </button> */}
                      <button
                        className={`${chartPeriod === "1Y" ? "dash-btn-active" : ""}`}
                        style={{
                          fontSize: "12px",
                          border: "2px solid #D5DCE0",
                          padding: "5px 15px",
                          borderRadius: "5px",
                        }}
                        onClick={() => setChartPeriod("1Y")}
                      >
                        1Y
                      </button>
                    </div>

                    {/* Year Dropdown (for 1Y and 1M) */}
                    {(chartPeriod === "1Y" || chartPeriod === "1M") && (
                      <select
                        value={selectedYear}
                        onChange={(e) =>
                          setSelectedYear(Number(e.target.value))
                        }
                        className="form-select form-select-sm"
                        style={{
                          width: "75px",
                          fontSize: "12px",
                          padding: "5px 15px",
                        }}
                      >
                        {yearOptions.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    )}

                    {/* Month Dropdown (only for 1M) */}
                    {chartPeriod === "1M" && (
                      <select
                        value={selectedMonth}
                        onChange={(e) =>
                          setSelectedMonth(Number(e.target.value))
                        }
                        className="form-select form-select-sm"
                        style={{
                          width: "65px",
                          fontSize: "12px",
                          padding: "5px 15px",
                        }}
                      >
                        {monthOptions.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                {loading ? (
                  <div
                    className="d-flex align-items-center justify-content-center "
                    style={{ height: "300px" }}
                  >
                    Loading...
                  </div>
                ) : chartData.series.length > 0 ? (
                  <PerformanceChart
                    title=""
                    labels={chartData.labels}
                    series={chartData.series}
                  />
                ) : (
                  <div className="text-center py-5 text-muted">
                    No data available
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RECENT ORDERS TABLE */}
          <div className="row mt-24 ">
            <DashboardOrders />
          </div>
        </section>
      </div>
    </AdminHeader>
  );
};;;

export default Dashboard;

const GrowthIndicator = ({ value }) => {
  const isPositive = value > 0;
  const isNegative = value < 0;

    function formatPercent(value) {
      const sign = value > 0 ? "+" : "";
      return `${sign}${value}%`;
    }

  return (
    <div className="mt-5 d-flex align-items-center gap-1">
      {value !== 0 && (
        <span
          className={isPositive ? "dash-arrow-up" : "dash-arrow-down"}
          style={{
            color: isPositive ? "#28a745" : "#dc3545",
            fontSize: "12px",
          }}
        />
      )}

      <span
        style={{
          color: isPositive
            ? "#28a745"
            : isNegative
            ? "#dc3545"
            : "#6c757d",
          fontWeight: 600,
        }}
      >
        {formatPercent(value)}
      </span>
    </div>
  );
};
